import { getIdToken } from './firebase'

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.sigmanote.app/api'

async function getHeaders() {
  const token = await getIdToken()
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

async function handleResponse(response) {
  if (response.status === 401) {
    throw new Error('Session expired. Please sign in again.')
  }
  if (response.status === 450) {
    throw new Error('Free plan limit reached. Please upgrade to Premium.')
  }
  if (!response.ok) {
    const error = await response.text()
    throw new Error(error || `Request failed: ${response.status}`)
  }
  return response.json()
}

// Spaces API
export async function listSpaces() {
  const response = await fetch(`${API_BASE}/spaces/second/list`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function getSpaceContent(spaceId) {
  const response = await fetch(`${API_BASE}/spaces/${spaceId}/children`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function getSpace(spaceId) {
  const response = await fetch(`${API_BASE}/spaces/${spaceId}`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function createSpace(name, parentId = null) {
  const response = await fetch(`${API_BASE}/spaces`, {
    method: 'POST',
    headers: await getHeaders(),
    body: JSON.stringify({ name, parentId }),
  })
  return handleResponse(response)
}

export async function updateSpace(spaceId, { name }) {
  const response = await fetch(`${API_BASE}/spaces/${spaceId}`, {
    method: 'PUT',
    headers: await getHeaders(),
    body: JSON.stringify({ name }),
  })
  return handleResponse(response)
}

export async function deleteSpace(spaceId) {
  const response = await fetch(`${API_BASE}/spaces/${spaceId}`, {
    method: 'DELETE',
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function moveSpace(spaceId, newParentId) {
  const response = await fetch(`${API_BASE}/spaces/${spaceId}/move`, {
    method: 'POST',
    headers: await getHeaders(),
    body: JSON.stringify({ newParentId }),
  })
  return handleResponse(response)
}

export async function moveContentToSpace(contentIds, newSpaceId) {
  const response = await fetch(`${API_BASE}/spaces/${newSpaceId}/content`, {
    method: 'POST',
    headers: await getHeaders(),
    body: JSON.stringify({ contentIds: Array.isArray(contentIds) ? contentIds : [contentIds] }),
  })
  return handleResponse(response)
}

export async function bulkDeleteContent(contentIds) {
  const response = await fetch(`${API_BASE}/content/bulk`, {
    method: 'DELETE',
    headers: await getHeaders(),
    body: JSON.stringify({ contentIds }),
  })
  return handleResponse(response)
}

// Content API
export async function listContents(filters = {}) {
  const params = new URLSearchParams()
  if (filters.types && filters.types.length > 0) {
    params.append('types', filters.types.join(','))
  }
  if (filters.spaceId) {
    params.append('space_id', filters.spaceId)
  }
  if (filters.startTime) {
    params.append('start_time', filters.startTime)
  }
  if (filters.endTime) {
    params.append('end_time', filters.endTime)
  }
  const url = params.toString()
    ? `${API_BASE}/content/list?${params.toString()}`
    : `${API_BASE}/content/list`
  const response = await fetch(url, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function getContent(contentId) {
  const response = await fetch(`${API_BASE}/content/${contentId}`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function getRawContent(contentId) {
  const response = await fetch(`${API_BASE}/content/${contentId}?raw=true`, {
    headers: await getHeaders(),
  })
  if (!response.ok) {
    throw new Error(`Failed to get raw content: ${response.status}`)
  }
  return response.text()
}

export async function getRecentContents() {
  const response = await fetch(`${API_BASE}/content/recent`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function createContent({ title, content, spaceId, type = 'note', url, description }) {
  const formData = new FormData()
  formData.append('contentInput', JSON.stringify({
    title,
    type,
    content_text: content,
    space_id: spaceId,
    url,
    description,
  }))
  
  const token = await getIdToken()
  const response = await fetch(`${API_BASE}/content`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  })
  return handleResponse(response)
}

export async function deleteContent(contentId) {
  const response = await fetch(`${API_BASE}/content/${contentId}`, {
    method: 'DELETE',
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function updateContent({ contentId, title, content, description, image }) {
  const response = await fetch(`${API_BASE}/content/update`, {
    method: 'PUT',
    headers: await getHeaders(),
    body: JSON.stringify({
      content_id: contentId,
      title,
      content_text: content,
      description,
      image,
    }),
  })
  return handleResponse(response)
}

export async function renameContent(contentId, title) {
  const response = await fetch(`${API_BASE}/content/${contentId}/rename`, {
    method: 'POST',
    headers: await getHeaders(),
    body: JSON.stringify({ title }),
  })
  return handleResponse(response)
}

export async function uploadFile({ file, title, spaceId, type }) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('contentInput', JSON.stringify({
    title: title || file.name,
    type: type || file.name.split('.').pop(),
    space_id: spaceId,
  }))
  
  const token = await getIdToken()
  const response = await fetch(`${API_BASE}/content`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  })
  return handleResponse(response)
}

export async function importUrl({ url, spaceId }) {
  const formData = new FormData()
  formData.append('contentInput', JSON.stringify({
    type: 'bookmark',
    url,
    space_id: spaceId,
  }))
  
  const token = await getIdToken()
  const response = await fetch(`${API_BASE}/content`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  })
  return handleResponse(response)
}

// Search API
const SEARCH_API_BASE = import.meta.env.VITE_SEARCH_API_URL || 'https://s8.signifyid.com/api'

export async function searchContents(query, filters = {}) {
  const params = new URLSearchParams()
  if (query) params.append('query', query)
  params.append('topK', '20')
  params.append('includeChunk', 'true')
  if (filters.type) params.append('type', filters.type)
  if (filters.spaceId) params.append('spaceId', filters.spaceId)
  if (filters.dateFrom) params.append('dateFrom', filters.dateFrom)
  if (filters.dateTo) params.append('dateTo', filters.dateTo)

  const response = await fetch(`${SEARCH_API_BASE}/search?${params.toString()}`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

// Content actions
export async function reimportContent(contentId) {
  const response = await fetch(`${API_BASE}/content/${contentId}/reimport`, {
    method: 'POST',
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function shareContent(contentId) {
  const response = await fetch(`${API_BASE}/content/share`, {
    method: 'POST',
    headers: await getHeaders(),
    body: JSON.stringify({ contentId }),
  })
  return handleResponse(response)
}

export async function getContentInfo(contentId) {
  const response = await fetch(`${API_BASE}/content/${contentId}/info`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function renameChatThread(threadId, name) {
  const response = await fetch(`${API_BASE}/chat/threads/${threadId}`, {
    method: 'PUT',
    headers: await getHeaders(),
    body: JSON.stringify({ name }),
  })
  return handleResponse(response)
}

// Chat API
export async function listChatThreads() {
  const response = await fetch(`${API_BASE}/chat/threads`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function getChatThread(threadId) {
  const response = await fetch(`${API_BASE}/chat/threads/${threadId}`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function createChatThread({ name, contentIds, question }) {
  const response = await fetch(`${API_BASE}/chat/threads`, {
    method: 'POST',
    headers: await getHeaders(),
    body: JSON.stringify({
      name,
      contentIds,
      question,
      llmConfig: { provider: 'google', model: 'gemini-1.5-flash' },
    }),
  })
  return handleResponse(response)
}

export async function deleteChatThread(threadId) {
  const response = await fetch(`${API_BASE}/chat/threads/${threadId}`, {
    method: 'DELETE',
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

export async function updateChatThread(threadId, updates) {
  const response = await fetch(`${API_BASE}/chat/threads/${threadId}`, {
    method: 'PUT',
    headers: await getHeaders(),
    body: JSON.stringify(updates),
  })
  return handleResponse(response)
}

// Streaming chat
export async function* streamChat(threadId, message, selection = null) {
  const token = await getIdToken()
  console.log('streamChat called with threadId:', threadId, 'message:', message)
  const response = await fetch(`${API_BASE}/chat/threads/${threadId}/chat`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question: message,
      llmConfig: { provider: 'google', model: 'gemini-1.5-flash' },
      ...(selection && { selectedContext: selection }),
    }),
  })

  console.log('streamChat response status:', response.status)
  if (!response.ok) {
    throw new Error(`Chat request failed: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) {
      console.log('streamChat done, final buffer:', buffer)
      break
    }

    buffer += decoder.decode(value, { stream: true })
    console.log('streamChat buffer:', buffer.substring(0, 200))
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      console.log('streamChat line:', line.substring(0, 100))
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6))
          console.log('streamChat yielding data:', data)
          yield data
        } catch (e) {
          console.log('streamChat JSON parse error:', e, 'line:', line)
        }
      }
    }
  }
}

// User API - includes subscription and usage info
export async function getCurrentUser() {
  const response = await fetch(`${API_BASE}/user`, {
    headers: await getHeaders(),
  })
  return handleResponse(response)
}

// Subscription API
export async function createCheckoutSession(priceId, successUrl, cancelUrl) {
  const response = await fetch(`${API_BASE}/subscription/create-checkout-session`, {
    method: 'POST',
    headers: await getHeaders(),
    body: JSON.stringify({
      priceId,
      successUrl: successUrl || `${window.location.origin}/settings?subscription=success`,
      cancelUrl: cancelUrl || `${window.location.origin}/settings?subscription=cancelled`,
    }),
  })
  return handleResponse(response)
}

export async function createPortalSession(returnUrl) {
  const response = await fetch(`${API_BASE}/subscription/create-portal-session`, {
    method: 'POST',
    headers: await getHeaders(),
    body: JSON.stringify({
      returnUrl: returnUrl || `${window.location.origin}/settings`,
    }),
  })
  return handleResponse(response)
}
